import { Box, DrawerSlotsAndSlotProps, Stack, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';

import { SlidingPanel, SlidingPanelContent } from 'core/components';
import { inlineFormStickySxProps } from 'core/constants';
import { NotificationPosition } from 'core/enums';
import { useNotificationContext, useStepsContext } from 'core/states';
import { IStep } from 'core/types';
import { Color, Size, Variant } from 'theme/enums';

import StepButtons from './StepButtons';
import { Progress, StepWrapper } from './style';

interface IProps {
  title?: string;
  subtitle?: string;
  isOpen: boolean;
  color?: Color;
  size?: Size;
  fullWidth?: boolean;
  fullScreen?: boolean;
  interactive?: boolean;
  drawerSlotAndSlotProps?: DrawerSlotsAndSlotProps;
  contentStyle?: SxProps;
  notificationPosition?: NotificationPosition;
  level?: number;
  // On top of the drawer's own loading notification, which any other toast can replace.
  disableClose?: boolean;
}

export const Steps = ({
  title,
  subtitle,
  isOpen,
  color = Color.PRIMARY,
  size,
  fullWidth = true,
  fullScreen = false,
  interactive = false,
  drawerSlotAndSlotProps = {},
  contentStyle = {},
  notificationPosition = NotificationPosition.DRAWER,
  level = 0,
  disableClose = false,
}: IProps) => {
  const { steps, activeStep, activeStepIndex, totalSteps, handleOnClose } = useStepsContext();
  const { getIsLoadingNotificationDisplayed } = useNotificationContext();

  const hasSingleStep = totalSteps === 1;

  const StepTitle = useMemo(
    () => (
      <Stack gap={1}>
        <Box>
          <Trans
            i18nKey="titleStep"
            values={{
              stepIndex: activeStepIndex + 1,
              stepsCount: totalSteps,
              stepTitle: activeStep.title,
            }}
            components={{
              countWrapper: <Typography color={Color.NEUTRAL} />,
            }}
          />
        </Box>
      </Stack>
    ),
    [activeStep.title, activeStepIndex, totalSteps]
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={() => handleOnClose()}
      color={color}
      title={title}
      subtitle={subtitle}
      size={size}
      interactive={interactive}
      autoWidth={fullWidth}
      fullScreen={fullScreen}
      disableAutoClose
      disableClose={disableClose || getIsLoadingNotificationDisplayed(NotificationPosition.DRAWER)}
      drawerSlotAndSlotProps={drawerSlotAndSlotProps}
      contentStyle={contentStyle}
      level={level}
    >
      {isOpen && (
        <>
          {steps.map((step: IStep) => {
            if (activeStep?.type !== step?.type) {
              return;
            }

            return (
              <SlidingPanelContent
                key={`${step.type}-${step?.title?.toString()}`}
                title={!hasSingleStep ? StepTitle : null}
                subtitle={step?.subtitle}
                headline={
                  !hasSingleStep && (
                    <Progress
                      variant={Variant.SOLID}
                      determinate
                      value={((activeStepIndex + 1) / totalSteps) * 100}
                    />
                  )
                }
                buttons={<StepButtons step={step} />}
                fullWidth={fullWidth}
                fullScreen={fullScreen}
                notificationPosition={notificationPosition}
                stickyHeader
                stickySxProps={
                  steps?.length > 1
                    ? inlineFormStickySxProps
                    : { notification: { marginBottom: '24px' } }
                }
                sx={step.sx}
              >
                <StepWrapper>{step.content}</StepWrapper>
              </SlidingPanelContent>
            );
          })}
        </>
      )}
    </SlidingPanel>
  );
};
